import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {DataService} from './data.service';

export interface GlEntriesFilters {
  fromDate?: string | Date | null;
  toDate?: string | Date | null;
  companyId?: string;
  locationId?: string;
  entityId?: string;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface GlEntrySummaryItem {
  assetId: string;
  assetName: string;
  amount: number;
  expenseAccountId?: string;
  accumAccountId?: string;
}

export interface RegisterFilters {
  fromDate?: string | Date | null;
  toDate?: string | Date | null;
  companyId?: string;
  locationId?: string;
  entityId?: string;
  active?: boolean;
  search?: string;
  category?: string;
  method?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface RegisterRowItem {
  id: string;
  name: string;
  assetTag?: string;
  category?: string;
  companyName?: string;
  locationName?: string;
  entityName?: string; // vendor/entity name
  acquisitionDate?: string;
  acquisitionCost: number;
  accumulatedDepreciation: number;
  macrsAccumulated?: number | null;
  netBookValue: number;
  active: boolean;
  lastPostedDepDate?: string;
  // optional future field if backend provides it
  netBookValueMacrs?: number | null;
}

export interface DisposalsFilters {
  fromDate?: string | Date | null;
  toDate?: string | Date | null;
  companyId?: string;
  locationId?: string;
  entityId?: string;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface DisposalRowItem {
  id: string;
  name: string;
  disposalDate?: string;
  proceeds: number;
  expenses?: number | null;
  gainLoss: number;
}

export interface DepDetailFilters {
  fromDate?: string | Date | null;
  toDate?: string | Date | null;
  companyId?: string;
  locationId?: string;
  entityId?: string;
  search?: string;
  category?: string;
  method?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface DepDetailRowItem {
  assetId: string;
  assetName?: string;
  assetTag?: string;
  category?: string;
  companyName?: string;
  locationName?: string;
  entityName?: string; // vendor/entity name
  // Depreciation method used for this asset (passthrough from API)
  depreciationMethod?: string | number;
  periodStart: string;
  periodEnd: string;
  periodNumber: number;
  bookDepreciation: number;
  bookAccumulated: number;
  endingNBV: number;
  macrsDepreciation?: number | null;
  macrsAccumulated?: number | null;
}

@Injectable({providedIn: 'root'})
export class FixedAssetReportsApiService {
  private base = 'fixed-assets/reports';

  constructor(private api: DataService) {
  }

  /** Fetch GL Entries report (server-paged). Returns simple summary items suitable for the current UI. */
  getGlEntries(page: number, pageSize: number, f: GlEntriesFilters = {}): Observable<{ items: GlEntrySummaryItem[]; total: number }> {
    const q: string[] = [];
    if (f.search) {
      q.push(`search=${encodeURIComponent(String(f.search))}`);
    }
    if (f.fromDate) {
      q.push(`fromDate=${encodeURIComponent(new Date(f.fromDate).toISOString())}`);
    }
    if (f.toDate) {
      q.push(`toDate=${encodeURIComponent(new Date(f.toDate).toISOString())}`);
    }
    if (f.companyId) {
      q.push(`companyId=${encodeURIComponent(String(f.companyId))}`);
    }
    if (f.locationId) {
      q.push(`locationId=${encodeURIComponent(String(f.locationId))}`);
    }
    if (f.entityId) {
      q.push(`entityId=${encodeURIComponent(String(f.entityId))}`);
    }
    if (f.sortBy) {
      q.push(`sortBy=${encodeURIComponent(String(f.sortBy))}`);
    }
    if (f.sortDir) {
      q.push(`sortDir=${encodeURIComponent(String(f.sortDir))}`);
    }
    q.push(`pageNumber=${Math.max(0, page - 1)}`);
    q.push(`pageSize=${pageSize}`);

    const params = q.join('&');
    const url = `${this.base}/gl-entries`;
    return this.api.getDataWithParams(url, params).pipe(
      map((res: any) => {
        const raw = res || {};
        const arr: any[] = Array.isArray(raw?.Data) ? raw.Data : [];
        const items: GlEntrySummaryItem[] = arr.map((r: any) => ({
          assetId: String(r.AssetId ?? r.assetId ?? ''),
          assetName: String(r.AssetName ?? r.assetName ?? ''),
          amount: Number(r.Amount ?? r.amount ?? 0),
          expenseAccountId: r.DebitAccountId ?? r.expenseAccountId ?? r.debitAccountId,
          accumAccountId: r.CreditAccountId ?? r.accumAccountId ?? r.creditAccountId,
        }));
        const total = Number(raw?.TotalRecords ?? raw?.totalRecords ?? items.length);
        return {items, total};
      })
    );
  }

  /** Fetch Depreciation Detail rows (server-paged). */
  getDepreciationDetail(page: number, pageSize: number, f: DepDetailFilters = {}): Observable<{
    items: DepDetailRowItem[];
    total: number
  }> {
    const q: string[] = [];
    if (f.search) {
      q.push(`search=${encodeURIComponent(String(f.search))}`);
    }
    if (f.fromDate) {
      q.push(`fromDate=${encodeURIComponent(new Date(f.fromDate).toISOString())}`);
    }
    if (f.toDate) {
      q.push(`toDate=${encodeURIComponent(new Date(f.toDate).toISOString())}`);
    }
    if (f.companyId) {
      q.push(`companyId=${encodeURIComponent(String(f.companyId))}`);
    }
    if (f.locationId) {
      q.push(`locationId=${encodeURIComponent(String(f.locationId))}`);
    }
    if (f.entityId) {
      q.push(`entityId=${encodeURIComponent(String(f.entityId))}`);
    }
    if (f.category) {
      q.push(`category=${encodeURIComponent(String(f.category))}`);
    }
    if (f.method) {
      q.push(`method=${encodeURIComponent(String(f.method))}`);
    }
    if (f.sortBy) {
      q.push(`sortBy=${encodeURIComponent(String(f.sortBy))}`);
    }
    if (f.sortDir) {
      q.push(`sortDir=${encodeURIComponent(String(f.sortDir))}`);
    }
    q.push(`pageNumber=${Math.max(0, page - 1)}`);
    q.push(`pageSize=${pageSize}`);
    const params = q.join('&');
    const url = `${this.base}/depreciation-detail`;
    return this.api.getDataWithParams(url, params).pipe(
      map((res: any) => {
        const raw = res as any;
        const arr: any[] = Array.isArray(raw?.Data) ? raw.Data : [];
        const items: DepDetailRowItem[] = arr.map((r: any) => ({
          assetId: String(r.AssetId ?? r.assetId ?? ''),
          assetName: r.AssetName ?? r.assetName,
          assetTag: r.AssetTag ?? r.assetTag,
          category: r.Category ?? r.category,
          companyName: r.CompanyName ?? r.companyName,
          locationName: r.LocationName ?? r.locationName,
          entityName: r.EntityName ?? r.entityName ?? r.VendorName ?? r.vendorName,
          depreciationMethod: (r.DepreciationMethod ?? r.depreciationMethod ?? r.Method ?? r.method),
          periodStart: r.PeriodStart ?? r.periodStart,
          periodEnd: r.PeriodEnd ?? r.periodEnd,
          periodNumber: Number(r.PeriodNumber ?? r.periodNumber ?? 0),
          bookDepreciation: Number(r.BookDepreciation ?? r.bookDepreciation ?? 0),
          bookAccumulated: Number(r.BookAccumulated ?? r.bookAccumulated ?? 0),
          endingNBV: Number(r.EndingNBV ?? r.endingNBV ?? r.EndingBookValue ?? r.endingBookValue ?? 0),
          macrsDepreciation: r.MacrsDepreciation ?? r.macrsDepreciation ?? null,
          macrsAccumulated: r.MacrsAccumulated ?? r.macrsAccumulated ?? null,
        }));
        const total = Number(raw?.totalRecords ?? raw?.TotalRecords ?? 0);
        return {items, total};
      })
    );
  }

  /** Fetch Asset Register (server-paged). */
  getRegister(page: number, pageSize: number, f: RegisterFilters = {}): Observable<{ items: RegisterRowItem[]; total: number }> {
    const q: string[] = [];
    if (f.search) {
      q.push(`search=${encodeURIComponent(String(f.search))}`);
    }
    if (f.fromDate) {
      q.push(`fromDate=${encodeURIComponent(new Date(f.fromDate).toISOString())}`);
    }
    if (f.toDate) {
      q.push(`toDate=${encodeURIComponent(new Date(f.toDate).toISOString())}`);
    }
    if (f.companyId) {
      q.push(`companyId=${encodeURIComponent(String(f.companyId))}`);
    }
    if (f.locationId) {
      q.push(`locationId=${encodeURIComponent(String(f.locationId))}`);
    }
    if (f.entityId) {
      q.push(`entityId=${encodeURIComponent(String(f.entityId))}`);
    }
    if (typeof f.active === 'boolean') {
      q.push(`active=${f.active ? 'true' : 'false'}`);
    }
    if (f.category) {
      q.push(`category=${encodeURIComponent(String(f.category))}`);
    }
    if (f.method) {
      q.push(`method=${encodeURIComponent(String(f.method))}`);
    }
    if (f.sortBy) {
      q.push(`sortBy=${encodeURIComponent(String(f.sortBy))}`);
    }
    if (f.sortDir) {
      q.push(`sortDir=${encodeURIComponent(String(f.sortDir))}`);
    }
    q.push(`pageNumber=${Math.max(0, page - 1)}`);
    q.push(`pageSize=${pageSize}`);
    const params = q.join('&');
    const url = `${this.base}/register`;
    return this.api.getDataWithParams(url, params).pipe(
      map((res: any) => {
        const raw = res || {};
        const arr: any[] = Array.isArray(raw?.Data) ? raw.Data : [];
        const items: RegisterRowItem[] = arr.map((r: any) => ({
          id: String(r.Id ?? r.id ?? ''),
          name: String(r.Name ?? r.name ?? ''),
          assetTag: r.AssetTag ?? r.assetTag,
          category: r.Category ?? r.category,
          companyName: r.CompanyName ?? r.companyName,
          locationName: r.LocationName ?? r.locationName,
          entityName: r.EntityName ?? r.entityName ?? r.VendorName ?? r.vendorName,
          acquisitionDate: r.AcquisitionDate ?? r.acquisitionDate,
          acquisitionCost: Number(r.AcquisitionCost ?? r.acquisitionCost ?? 0),
          accumulatedDepreciation: Number(r.AccumulatedDepreciation ?? r.accumulatedDepreciation ?? 0),
          // Accept either MacrsAccumulated (old) or AccumulatedMacrs (new payload shape)
          macrsAccumulated: (r.MacrsAccumulated ?? r.AccumulatedMacrs ?? r.macrsAccumulated ?? r.accumulatedMacrs ?? null) as number | null,
          netBookValue: Number(r.NetBookValue ?? r.netBookValue ?? 0),
          active: !!(r.Active ?? r.active ?? true),
          lastPostedDepDate: r.LastPostedDepDate ?? r.lastPostedDepDate,
          netBookValueMacrs: r.NetBookValueMacrs ?? r.netBookValueMacrs ?? null,
        }));
        // Backend envelope uses TotalRecords (PascalCase); fall back to lower-case or items.length
        const total = Number(raw?.TotalRecords ?? raw?.totalRecords ?? raw?.totalrecords ?? items.length);
        return {items, total};
      })
    );
  }

  /** Fetch Disposals/Retirements (server-paged). */
  getDisposals(page: number, pageSize: number, f: DisposalsFilters = {}): Observable<{ items: DisposalRowItem[]; total: number }> {
    const q: string[] = [];
    if (f.search) {
      q.push(`search=${encodeURIComponent(String(f.search))}`);
    }
    if (f.fromDate) {
      q.push(`fromDate=${encodeURIComponent(new Date(f.fromDate).toISOString())}`);
    }
    if (f.toDate) {
      q.push(`toDate=${encodeURIComponent(new Date(f.toDate).toISOString())}`);
    }
    if (f.companyId) {
      q.push(`companyId=${encodeURIComponent(String(f.companyId))}`);
    }
    if (f.locationId) {
      q.push(`locationId=${encodeURIComponent(String(f.locationId))}`);
    }
    if (f.entityId) {
      q.push(`entityId=${encodeURIComponent(String(f.entityId))}`);
    }
    if (f.sortBy) {
      q.push(`sortBy=${encodeURIComponent(String(f.sortBy))}`);
    }
    if (f.sortDir) {
      q.push(`sortDir=${encodeURIComponent(String(f.sortDir))}`);
    }
    q.push(`pageNumber=${Math.max(0, page - 1)}`);
    q.push(`pageSize=${pageSize}`);
    const params = q.join('&');
    const url = `${this.base}/disposals`;
    return this.api.getDataWithParams(url, params).pipe(
      map((res: any) => {
        const raw = res || {};
        const arr: any[] = Array.isArray(raw?.Data) ? raw.Data : [];
        const items: DisposalRowItem[] = arr.map((r: any) => ({
          id: String(r.Id ?? r.id ?? ''),
          name: String(r.Name ?? r.name ?? ''),
          disposalDate: r.DisposalDate ?? r.disposalDate,
          proceeds: Number(r.Proceeds ?? r.proceeds ?? 0),
          expenses: r.Expenses != null ? Number(r.Expenses) : (r.expenses != null ? Number(r.expenses) : null),
          gainLoss: Number(r.GainLoss ?? r.gainLoss ?? 0),
        }));
        const total = Number(raw?.TotalRecords ?? raw?.totalRecords ?? items.length);
        return {items, total};
      })
    );
  }
}
